/* assets/rod-header.js : sticky behaviour, mega menu, mobile menu drawer, account dropdown and
   the header cart/search behaviours (WP-2, SPEC 5.1–5.4). Loaded by sections/rod-header.liquid. */
import { Rod } from '@rod/core';
import { lockScroll, unlockScroll, requestIdleCallback } from '@theme/utilities';

const doc = document;
const root = doc.documentElement;

const DESKTOP = window.matchMedia('(min-width: 990px)');
const HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
const LANDSCAPE = window.matchMedia('(max-height: 499px) and (orientation: landscape) and (max-width: 989px)');
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

const SCROLL_DELTA = 8;
const HIDE_AFTER = 40;
const CLOSE_DELAY = 250;
const RAIL_DELAY = 100;
const PANEL_MS = 260;
const SUPPRESS_MS = 900;
const SUPPRESS_CHAIN_MS = 400;
const PAGESHOW_SUPPRESS_MS = 1000;
const RETRY_COOLDOWN = 10000;
// A chevron clicked this soon after its panel was opened by hover keeps the panel open instead of
// closing it (review fix round 2): sweeping the nav and then clicking the only control that looks
// like a menu opener must not make the menu vanish.
const HOVER_CLICK_MS = 600;

const SUPPRESS_KEYS = new Set([
  'Tab',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Spacebar',
]);

const TEXT_INPUT = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

const MSG = {
  emptySearch: 'Lütfen aramak istediğiniz kelimeyi yazın',
  panelsError: 'Kategoriler yüklenemedi.',
  retry: 'Tekrar dene',
};

/** Computed --rod-hover-intent in ms (0 under reduced motion). */
function hoverIntentMs() {
  const raw = getComputedStyle(root).getPropertyValue('--rod-hover-intent').trim();
  if (raw.endsWith('ms')) return parseFloat(raw) || 0;
  if (raw.endsWith('s')) return (parseFloat(raw) || 0) * 1000;
  return 150;
}

function setPx(name, px) {
  root.style.setProperty(name, `${Math.max(0, Math.round(px))}px`);
}

function rectHeight(el) {
  return el ? el.getBoundingClientRect().height : 0;
}

function isTextInput(node) {
  return node instanceof Element && node.matches(TEXT_INPUT);
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class RodHeader extends HTMLElement {
  /* --- refs --- */
  #announce = null;
  #announceClose = null;
  #main = null;
  #searchRow = null;
  #nav = null;
  #navList = null;
  #panelHost = null;
  #overlay = null;
  #menu = null;
  #menuBody = null;
  #menuBtn = null;
  #menuBack = null;
  #menuClose = null;
  #menuTitle = null;
  #account = null;
  #cartBtn = null;
  #cartIcon = null;

  /* --- state --- */
  #lastY = 0;
  #accum = 0;
  #searchHidden = false;
  #suppressUntil = 0;
  #headerHeight = 56;
  #lastBottom = -1;
  #panelsState = 'idle';
  #errorAt = 0;
  #openHandle = null;
  #hoverTimer = 0;
  #closeTimer = 0;
  #railTimer = 0;
  #bumpTimer = 0;
  #lastCount = null;
  #touch = false;
  #openedByHover = 0;
  #switching = false;
  #pendingMega = null;
  #pendingLevel = null;
  #menuStack = [];
  #menuPending = null;
  #enterTimer = 0;
  #cleanup = [];

  connectedCallback() {
    if (this.dataset.rodReady === 'true') return;
    this.dataset.rodReady = 'true';

    this.#announce = this.querySelector('[data-rod-announce]');
    this.#announceClose = this.querySelector('[data-rod-announce-close]');
    this.#main = this.querySelector('.rod-hdr__main');
    this.#searchRow = this.querySelector('[data-rod-search-row]');

    // WP-77 — telefonda arama satiri artik butonla aciliyor. Satir sunucudan
    // `hidden` geliyor; JS yoksa hic acilmamasi yerine gorunur kalmasi daha
    // dogru oldugu icin burada hidden kaldirilip kapali duruma CSS ile geciliyor.
    // Boylece JS calismadiginda arama kutusu erisilebilir kaliyor.
    const aramaAc = this.querySelector('[data-rod-search-toggle]');
    const aramaKapat = this.querySelector('[data-rod-search-close]');
    if (this.#searchRow && aramaAc) {
      // Satir sunucudan `hidden` geliyor; JS baglaninca hidden kalkip kapali
      // duruma SINIFLA geciliyor. Boylece JS calismazsa arama kutusu gorunur
      // ve kullanilabilir kalir (WP-77).
      this.#searchRow.hidden = false;
      this.#searchRow.classList.add('is-collapsed');
      const girdi = this.#searchRow.querySelector('[data-rod-search-input]');

      const durum = (acik) => {
        this.#searchRow.classList.toggle('is-collapsed', !acik);
        aramaAc.setAttribute('aria-expanded', acik ? 'true' : 'false');
        if (acik) girdi?.focus();
        else aramaAc.focus();
        this.measure?.();
      };

      // ASIL YOL: temanin kendi tam ekran arama modali (#search-modal, her sayfada
      // basiliyor). Icinde predictive search ve rod-search.js'in Turkce duyarli
      // "son aramalar" listesi hazir. Kendi panelimizi yazmak ayni isi ikinci kez
      // yapmak olurdu ve iki ayri son-arama deposu ortaya cikardi.
      // YEDEK: modal yoksa (ya da acilamiyorsa) satiri ac; hicbir sey olmamasindan iyi.
      aramaAc.addEventListener('click', () => {
        const modal = document.getElementById('search-modal');
        if (modal && typeof modal.showDialog === 'function') { modal.showDialog(); return; }
        durum(this.#searchRow.classList.contains('is-collapsed'));
      });
      aramaKapat?.addEventListener('click', () => durum(false));
      this.#searchRow.addEventListener('keydown', (e) => { if (e.key === 'Escape') durum(false); });
    }
    this.#nav = this.querySelector('[data-rod-nav]');
    this.#navList = this.querySelector('.rod-hdr__nav-list');
    this.#panelHost = this.querySelector('[data-rod-nav-panels-host]');
    this.#overlay = this.querySelector('[data-rod-mega-overlay]');
    this.#menu = this.querySelector('[data-rod-menu]');
    this.#menuBody = this.querySelector('[data-rod-menu-body]');
    this.#menuBtn = this.querySelector('[data-rod-menu-open]');
    this.#menuBack = this.querySelector('[data-rod-menu-back]');
    this.#menuClose = this.querySelector('[data-rod-menu-close]');
    this.#menuTitle = this.querySelector('[data-rod-menu-title]');
    this.#account = this.querySelector('[data-rod-account]');
    this.#cartBtn = this.querySelector('.rod-hdr__cart');
    this.#cartIcon = this.querySelector('.rod-hdr__cart-icon');

    this.#setupMetrics();
    this.#setupScroll();
    this.#setupAnnouncement();
    this.#setupSearchForms();
    this.#setupNav();
    this.#setupMenu();
    this.#setupAccount();
    this.#setupCart();
    this.#setupLoginLinks();
    this.#setupPanelTriggers();
  }

  disconnectedCallback() {
    for (const fn of this.#cleanup) {
      try {
        fn();
      } catch (e) {
        /* no-op */
      }
    }
    this.#cleanup = [];
  }

  #on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.#cleanup.push(() => target.removeEventListener(type, handler, options));
  }

  /* ============================================================ metrics (5.1.1) */
  #setupMetrics() {
    this.#measure();
    this.#updateHeaderBottom();

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => {
        this.#measure();
        this.#updateHeaderBottom();
      });
      for (const el of [this.#announce, this.#main, this.#searchRow, this.#nav]) {
        if (el) ro.observe(el);
      }
      this.#cleanup.push(() => ro.disconnect());
    }

    const onResize = Rod.debounce(() => {
      this.#measure();
      this.#updateHeaderBottom();
      this.#checkCompact();
      if (DESKTOP.matches) {
        this.#setSearchHidden(false, { silent: true });
        root.style.setProperty('--rod-search-row-visible', '0');
        this.#closeMenu();
      } else {
        this.#closeMega();
      }
    }, 150);
    this.#on(window, 'resize', onResize);
    this.#on(window, 'orientationchange', onResize);

    if (doc.fonts && doc.fonts.ready) {
      doc.fonts.ready.then(() => {
        this.#measure();
        this.#updateHeaderBottom();
        this.#checkCompact();
      });
    } else {
      this.#checkCompact();
    }

    root.style.setProperty('--rod-search-row-visible', DESKTOP.matches ? '0' : '1');
  }

  /* SPEC 15.1: the top utility bar is gone, so the only header row that scrolls away is the
     optional announcement. `--rod-topbar-h` is no longer measured, written or read by anything
     (the token itself is WP-0's rod-tokens.css; its removal is a cross-WP request). The sticky
     part is main row + nav on desktop and the main row on phones/tablets, which is what
     `--rod-header-sticky-h` (and through it `--rod-sticky-offset*`) publishes. */
  #measure() {
    const announce = rectHeight(this.#announce);
    const main = rectHeight(this.#main);
    const nav = rectHeight(this.#nav);
    const search = rectHeight(this.#searchRow);

    setPx('--rod-announce-h', announce);
    setPx('--rod-header-sticky-h', DESKTOP.matches ? main + nav : main);
    this.#headerHeight = announce + main + search;
    doc.body.style.setProperty('--header-height', `${Math.round(main)}px`);
  }

  #updateHeaderBottom() {
    let el = this.#main;
    if (DESKTOP.matches) {
      el = this.#nav || this.#main;
    } else if (this.#searchRow && !this.#searchHidden) {
      el = this.#searchRow;
    }
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const bottom = rect.height > 0 ? Math.max(0, Math.round(rect.bottom)) : 0;
    if (bottom === this.#lastBottom) return;
    this.#lastBottom = bottom;
    setPx('--rod-header-bottom', bottom);
  }

  /* ============================================================ scroll + search row (D10) */
  #setupScroll() {
    this.#lastY = window.scrollY;
    const onScroll = Rod.throttleRaf(() => this.#onScroll());
    this.#on(window, 'scroll', onScroll, { passive: true });

    // Reveal suppression (D10, 5.1.1, WCAG 2.4.11): a scroll that starts right after a focus move,
    // a navigation key, a hash change, an overlay close or a history restore is not a viewer gesture
    // and must not bring the search row back. wheel/touch/scrollbar drags are gestures and clear it.
    // While such a scroll is still running, every scroll event extends the window (a focus scroll can
    // last several frames). Hiding is never suppressed.
    const suppress = (ms) => {
      this.#suppressUntil = Math.max(this.#suppressUntil, Date.now() + ms);
    };
    const gesture = () => {
      this.#suppressUntil = 0;
    };

    this.#on(doc, 'focusin', () => suppress(SUPPRESS_MS), true);
    this.#on(
      doc,
      'keydown',
      (event) => {
        if (!SUPPRESS_KEYS.has(event.key)) return;
        if (event.key === ' ' && isTextInput(event.target)) return;
        suppress(SUPPRESS_MS);
      },
      true
    );
    this.#on(window, 'hashchange', () => suppress(SUPPRESS_MS));
    this.#on(doc, 'rod:overlay:closed', () => suppress(SUPPRESS_MS));
    this.#on(window, 'pageshow', () => {
      suppress(PAGESHOW_SUPPRESS_MS);
      this.#lastY = window.scrollY;
    });
    this.#on(window, 'wheel', gesture, { passive: true, capture: true });
    this.#on(window, 'touchmove', gesture, { passive: true, capture: true });
    this.#on(window, 'touchstart', gesture, { passive: true, capture: true });
    this.#on(window, 'mousedown', gesture, true);

    // Focus inside the row: show it at once, without the slide, so focus is never on a covered input.
    if (this.#searchRow) {
      this.#on(this.#searchRow, 'focusin', () => {
        this.classList.add('is-search-instant');
        this.#setSearchHidden(false);
        void this.#searchRow.offsetHeight;
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-search-instant')));
      });
    }

    suppress(PAGESHOW_SUPPRESS_MS);
    this.#onScroll();
  }

  #onScroll() {
    const y = Math.max(0, window.scrollY);
    // a non-gesture scroll in progress keeps the window alive for its whole run
    if (this.#suppressUntil && Date.now() <= this.#suppressUntil) this.#suppressUntil = Date.now() + SUPPRESS_CHAIN_MS;
    this.classList.toggle('is-scrolled', y > 0);
    if (!DESKTOP.matches) this.#updateSearchRow(y);
    this.#updateHeaderBottom();
    this.#lastY = y;
  }

  #updateSearchRow(y) {
    const headerH = this.#headerHeight || 108;
    const dy = y - this.#lastY;

    const focusInRow = !!this.#searchRow && this.#searchRow.contains(doc.activeElement);

    if (LANDSCAPE.matches) {
      // Landscape phones: hide once scrolled past the header and never reveal on scroll-up (D10).
      if (focusInRow && y > headerH) return;
      this.#setSearchHidden(y > headerH);
      return;
    }
    if (y < headerH) {
      this.#accum = 0;
      this.#setSearchHidden(false);
      return;
    }
    if (doc.body.classList.contains('rod-overlay-open')) return;
    if (focusInRow) return;

    if ((dy > 0 && this.#accum < 0) || (dy < 0 && this.#accum > 0)) this.#accum = 0;
    this.#accum += dy;

    if (this.#accum >= SCROLL_DELTA && y > headerH + HIDE_AFTER) {
      this.#accum = 0;
      this.#setSearchHidden(true);
    } else if (this.#accum <= -SCROLL_DELTA) {
      this.#accum = 0;
      if (Date.now() > this.#suppressUntil) this.#setSearchHidden(false);
    }
  }

  #setSearchHidden(hidden, { silent = false } = {}) {
    if (hidden === this.#searchHidden) return;
    this.#searchHidden = hidden;
    this.classList.toggle('is-search-hidden', hidden);
    // Keyboard focus must never land on the row while it is sliding out of view (WCAG 2.4.11):
    // the row is inert while hidden, so Tab skips it instead of focusing a covered input.
    if (this.#searchRow && 'inert' in HTMLElement.prototype) this.#searchRow.inert = hidden;
    root.style.setProperty('--rod-search-row-visible', hidden || DESKTOP.matches ? '0' : '1');
    this.#updateHeaderBottom();
    window.setTimeout(() => this.#updateHeaderBottom(), REDUCED.matches ? 0 : 220);
    if (silent) return;
    doc.dispatchEvent(
      new CustomEvent('rod:header:search-visibility', { detail: { visible: !hidden }, bubbles: true })
    );
  }

  /* ============================================================ announcement (5.1.2) */
  #setupAnnouncement() {
    if (!this.#announceClose || !this.#announce) return;
    this.#on(this.#announceClose, 'click', () => {
      const hash = this.#announce.dataset.rodAnnounceHash || '';
      Rod.store.set('rod:announce:dismissed', hash);
      root.setAttribute('data-rod-announce-dismissed', hash);
      this.#measure();
      this.#updateHeaderBottom();
    });
  }

  /* ============================================================ search forms (5.1.4) */
  #setupSearchForms() {
    for (const form of this.querySelectorAll('[data-rod-search-form]')) {
      this.#on(form, 'submit', (event) => {
        const input = form.querySelector('[data-rod-search-input]');
        const value = input ? String(input.value || '').trim() : '';
        if (value) return;
        event.preventDefault();
        if (input) input.focus();
        Rod.announce(MSG.emptySearch);
      });
    }
  }

  /* ============================================================ login return_url (5.4, T-ACC-03) */
  /** Liquid's `request` drop exposes only `path`, so a login started from a filtered PLP would come
      back to the unfiltered collection. Rewrite the header/drawer login links with the real current
      URL (path + query) once JS runs; without JS the Liquid `request.path` value stays (review fix
      round 2). */
  #setupLoginLinks() {
    const links = this.querySelectorAll('[data-rod-login]');
    if (!links.length) return;
    const query = window.location.search || '';
    const target = window.location.pathname + (query.length > 1 ? query : '');
    for (const link of links) {
      const href = link.getAttribute('href');
      if (!href) continue;
      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set('return_url', target);
        link.setAttribute('href', url.toString());
      } catch (e) {
        /* leave the server-rendered href */
      }
    }
  }

  /* ============================================================ lazy category panels (5.2.1) */
  #setupPanelTriggers() {
    const trigger = () => this.#loadPanels();
    if (this.#nav) {
      this.#on(this.#nav, 'pointerenter', trigger, { once: true });
      this.#on(this.#nav, 'focusin', trigger, { once: true });
    }
    if (this.#menuBtn) this.#on(this.#menuBtn, 'pointerdown', trigger, { once: true });

    const idlePreload = () => {
      if (!DESKTOP.matches) return;
      window.setTimeout(() => requestIdleCallback(trigger), 3000);
    };
    if (doc.readyState === 'complete') idlePreload();
    else this.#on(window, 'load', idlePreload, { once: true });
  }

  async #loadPanels({ force = false } = {}) {
    if (this.#panelsState === 'ready' || this.#panelsState === 'loading') return;
    this.#panelsState = 'loading';

    const version = this.dataset.rodNavVersion || '0';
    const key = `rod:nav:${version}`;
    let html = force ? null : Rod.session.get(key, null);
    if (typeof html !== 'string' || html.indexOf('data-rod-nav-panels') === -1) html = null;

    if (!html) {
      try {
        const base = (Rod.config.routes && Rod.config.routes.root) || '/';
        const res = await fetch(`${base}?section_id=rod-nav-panels`, {
          headers: { Accept: 'text/html' },
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const wrap = parsed.querySelector('[data-rod-nav-panels]');
        if (!wrap) throw new Error('no panels');
        html = wrap.outerHTML;
        for (const k of Rod.session.keys('rod:nav:')) {
          if (k !== key) Rod.session.remove(k);
        }
        Rod.session.set(key, html);
      } catch (e) {
        console.warn('[rod-header] category panels failed', e);
        this.#panelsState = 'error';
        this.#errorAt = Date.now();
        this.#renderPanelsError();
        return;
      }
    }

    this.#insertPanels(html);
  }

  #insertPanels(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const wrap = parsed.querySelector('[data-rod-nav-panels]');
    if (!wrap) {
      this.#panelsState = 'error';
      this.#renderPanelsError();
      return;
    }

    if (this.#panelHost) {
      for (const panel of wrap.querySelectorAll('[data-rod-nav-mega-set] > .rod-mega')) {
        if (!doc.getElementById(panel.id)) this.#panelHost.appendChild(doc.importNode(panel, true));
      }
    }
    if (this.#menuBody) {
      for (const panel of wrap.querySelectorAll('[data-rod-nav-drawer-set] > .rod-menu__panel')) {
        const handle = panel.getAttribute('data-rod-menu-panel');
        if (!handle) continue;
        if (!this.#menuBody.querySelector(`[data-rod-menu-panel="${CSS.escape(handle)}"]`)) {
          this.#menuBody.appendChild(doc.importNode(panel, true));
        }
      }
    }

    this.#panelsState = 'ready';
    this.#renderPromos();
    this.#bindPanels();
    this.#syncPanelStates();
    this.#syncAriaControls();

    if (this.#pendingMega) {
      // Keep the open OPTIONS with the pending handle: a keyboard open (Enter/Space/ArrowDown on the
      // chevron) that had to wait for the lazy fetch must still move focus into the rail once the
      // panel exists, otherwise the very first keyboard interaction of a session leaves focus on the
      // toggle and the panel content is unreachable (SPEC 5.2.3, T-MEGA-04).
      const pending = this.#pendingMega;
      this.#pendingMega = null;
      // Never steal focus: only pull it into the rail when the viewer is still sitting on the
      // toggle they pressed (a slow fetch must not yank focus back from wherever they moved on to).
      const onOpener = doc.activeElement === this.#openerFor(pending.handle);
      this.#openMega(pending.handle, { focusRail: pending.focusRail && onOpener, byHover: pending.byHover });
    }
    if (this.#pendingLevel) {
      const level = this.#pendingLevel;
      this.#pendingLevel = null;
      this.#clearMenuPending();
      this.#enterLevel(level.handle, level.title, { replacePending: true });
    }
  }

  #renderPanelsError() {
    const build = (klass) => {
      const box = doc.createElement('div');
      box.className = klass;
      const p = doc.createElement('p');
      p.textContent = MSG.panelsError;
      const btn = doc.createElement('button');
      btn.type = 'button';
      btn.className = 'rod-btn rod-btn--outline rod-btn--sm';
      btn.textContent = MSG.retry;
      btn.addEventListener('click', () => {
        this.#panelsState = 'idle';
        this.#restoreSkeletons();
        this.#loadPanels({ force: true });
      });
      box.append(p, btn);
      return box;
    };

    const pending = this.#panelHost ? this.#panelHost.querySelector('[data-rod-mega-pending]') : null;
    if (pending) pending.replaceChildren(build('rod-mega__error'));
    if (this.#menuPending) this.#menuPending.replaceChildren(build('rod-menu__error'));
  }

  #skeletonBox(klass) {
    const box = doc.createElement('div');
    box.className = klass;
    for (let i = 0; i < 6; i++) {
      const row = doc.createElement('div');
      row.className = 'rod-skeleton';
      box.appendChild(row);
    }
    return box;
  }

  /** Put the 6 skeleton rows back after "Tekrar dene", so a retry never leaves an empty panel. */
  #restoreSkeletons() {
    const pending = this.#panelHost ? this.#panelHost.querySelector('[data-rod-mega-pending]') : null;
    if (pending) pending.replaceChildren(this.#skeletonBox('rod-mega__pending'));
    if (this.#menuPending) this.#menuPending.replaceChildren(this.#skeletonBox('rod-menu__pending'));
  }

  #renderPromos() {
    const node = doc.getElementById('rod-mega-promos');
    if (!node) return;
    let promos = [];
    try {
      promos = JSON.parse(node.textContent || '[]');
    } catch (e) {
      return;
    }
    if (!Array.isArray(promos) || !promos.length) return;

    const byCategory = new Map();
    for (const promo of promos) {
      if (!promo || !promo.category) continue;
      const list = byCategory.get(promo.category) || [];
      if (list.length >= 2) continue;
      list.push(promo);
      byCategory.set(promo.category, list);
    }

    for (const [handle, list] of byCategory) {
      const holder = this.querySelector(`[data-rod-mega-promos="${CSS.escape(handle)}"]`);
      if (!holder) continue;
      holder.replaceChildren();
      for (const promo of list) {
        const card = doc.createElement(promo.link ? 'a' : 'div');
        card.className = 'rod-mega__promo';
        if (promo.link) card.href = promo.link;
        if (promo.image) {
          const img = doc.createElement('img');
          img.className = 'rod-mega__promo-img';
          img.src = promo.image;
          img.alt = promo.title || '';
          img.loading = 'lazy';
          card.appendChild(img);
        }
        const title = doc.createElement('p');
        title.className = 'rod-mega__promo-title';
        title.textContent = promo.title || '';
        card.appendChild(title);
        if (promo.text) {
          const text = doc.createElement('p');
          text.className = 'rod-mega__promo-text';
          text.textContent = promo.text;
          card.appendChild(text);
        }
        if (promo.link) {
          const cta = doc.createElement('span');
          cta.className = 'rod-mega__promo-cta';
          cta.append(promo.linkLabel || 'Keşfet', Rod.icon('arrow-right'));
          card.appendChild(cta);
        }
        holder.appendChild(card);
      }
      holder.hidden = false;
    }
  }

  /* ============================================================ category nav + mega (5.2) */
  #setupNav() {
    if (!this.#nav) return;

    this.#on(doc, 'pointerdown', (event) => {
      this.#touch = event.pointerType === 'touch';
    }, true);

    for (const item of this.#nav.querySelectorAll('.rod-hdr__nav-item')) {
      const opener = item.querySelector('[data-rod-mega-open]');
      if (!opener) continue;
      const handle = opener.getAttribute('data-rod-mega-open');
      const link = item.querySelector('a.rod-hdr__nav-link');
      const toggle = item.querySelector('.rod-hdr__nav-toggle');

      this.#on(item, 'pointerenter', (event) => {
        if (event.pointerType === 'touch' || !HOVER.matches) return;
        window.clearTimeout(this.#closeTimer);
        window.clearTimeout(this.#hoverTimer);
        if (this.#openHandle && this.#openHandle !== handle) {
          this.#openMega(handle, { byHover: true });
          return;
        }
        if (this.#openHandle === handle) return;
        this.#hoverTimer = window.setTimeout(() => this.#openMega(handle, { byHover: true }), hoverIntentMs());
      });

      this.#on(item, 'pointerleave', (event) => {
        if (event.pointerType === 'touch' || !HOVER.matches) return;
        window.clearTimeout(this.#hoverTimer);
        this.#scheduleMegaClose();
      });

      if (toggle) {
        this.#on(toggle, 'click', (event) => {
          event.preventDefault();
          this.#toggleFromClick(handle, event.detail === 0);
        });
        this.#on(toggle, 'keydown', (event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.#openMega(handle, { focusRail: true });
        });
      }

      if (link) {
        // Touch (>= 990): the first tap opens the panel, the second follows the link (5.2.3).
        this.#on(link, 'click', (event) => {
          if (!this.#touch || !DESKTOP.matches) return;
          if (this.#openHandle === handle) return;
          event.preventDefault();
          this.#openMega(handle);
        });
      } else {
        // "Tüm Kategoriler" is a button: it only opens.
        this.#on(opener, 'click', (event) => {
          if (opener.classList.contains('rod-hdr__nav-toggle')) return;
          event.preventDefault();
          this.#toggleFromClick(handle, event.detail === 0);
        });
      }
    }

    // Hover bridge on the PANEL HOST, not on the individual panels: the host also carries the
    // lazily created loading/error panel (which #bindPanels never sees), so moving the pointer from
    // a nav item down into a skeleton or into "Kategoriler yüklenemedi. / Tekrar dene" no longer
    // closes it 250 ms later and no longer cancels the deferred open (SPEC 5.2.1, T-HDR-12).
    if (this.#panelHost) {
      this.#on(this.#panelHost, 'pointerenter', () => window.clearTimeout(this.#closeTimer));
      this.#on(this.#panelHost, 'pointerleave', (event) => {
        if (event.pointerType === 'touch' || !HOVER.matches) return;
        this.#scheduleMegaClose();
      });
    }

    if (this.#overlay) {
      this.#on(this.#overlay, 'click', () => this.#closeMega());
    }

    this.#cleanup.push(
      Rod.onEsc(() => {
        if (!this.#openHandle) return false;
        const handle = this.#openHandle;
        this.#closeMega({ restoreFocus: false });
        const toggle = this.#openerFor(handle);
        if (toggle) toggle.focus();
        return true;
      })
    );
  }

  #openerFor(handle) {
    return this.querySelector(`[data-rod-mega-open="${CSS.escape(handle)}"]`);
  }

  /** Click on a nav opener (chevron or "Tüm Kategoriler"). A panel that a hover just opened stays
      open on the first click (review fix round 2); the next click closes it as SPEC 5.2.3 asks. */
  #toggleFromClick(handle, keyboard) {
    if (this.#openHandle === handle) {
      if (!keyboard && this.#openedByHover && Date.now() - this.#openedByHover < HOVER_CLICK_MS) {
        this.#openedByHover = 0;
        return;
      }
      this.#closeMega();
      return;
    }
    this.#openMega(handle, { focusRail: keyboard });
  }

  /** Hiding a panel that holds `document.activeElement` drops focus to <body> and the viewer loses
      their Tab position (review fix round 2). Move focus to the opener of `handle` first. In the
      compact nav (990–1199) the chevron is clipped, so its nav link takes the focus instead. */
  #rescueFocus(handle) {
    const active = doc.activeElement;
    if (!active || !this.#panelHost || !this.#panelHost.contains(active)) return;
    const opener = handle ? this.#openerFor(handle) : null;
    const item = opener ? opener.closest('.rod-hdr__nav-item') : null;
    const compact = !!this.#nav && this.#nav.classList.contains('is-compact');
    const target =
      (compact && item ? item.querySelector('a.rod-hdr__nav-link') : null) ||
      opener ||
      (this.#nav ? this.#nav.querySelector(FOCUSABLE) : null);
    if (!target) return;
    // The panel's own focusout must not read this move as "focus left the menu" and close it.
    this.#switching = true;
    try {
      target.focus({ preventScroll: true });
    } finally {
      this.#switching = false;
    }
  }

  /** `aria-controls` is set only once the referenced panel really exists in the document: the
      panels are lazy (5.2.1), so rendering the attribute in Liquid would leave every nav toggle
      pointing at a missing id on first paint (and forever if the fetch fails). */
  #syncAriaControls() {
    if (!this.#nav) return;
    for (const opener of this.#nav.querySelectorAll('[data-rod-mega-open]')) {
      const panel = this.#panelFor(opener.getAttribute('data-rod-mega-open'));
      if (panel && panel.id) opener.setAttribute('aria-controls', panel.id);
    }
  }

  #panelFor(handle) {
    return doc.getElementById(handle === 'all' ? 'rod-mega-all' : `rod-mega-${handle}`);
  }

  #scheduleMegaClose() {
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.#closeMega(), CLOSE_DELAY);
  }

  #megaPendingPanel({ reset = false } = {}) {
    if (!this.#panelHost) return null;
    let panel = this.#panelHost.querySelector('[data-rod-mega-pending]');
    if (panel) {
      if (reset) panel.replaceChildren(this.#skeletonBox('rod-mega__pending'));
      return panel;
    }
    panel = doc.createElement('div');
    panel.className = 'rod-mega';
    panel.setAttribute('data-rod-mega-pending', '');
    panel.hidden = true;
    panel.appendChild(this.#skeletonBox('rod-mega__pending'));
    this.#panelHost.appendChild(panel);
    return panel;
  }

  #openMega(handle, { focusRail = false, byHover = false } = {}) {
    if (!DESKTOP.matches || !this.#panelHost) return;
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#hoverTimer);

    let panel = this.#panelFor(handle);
    if (!panel) {
      if (this.#panelsState === 'ready') return;
      this.#pendingMega = { handle, focusRail, byHover };
      const failed = this.#panelsState === 'error';
      // After a failure, opening another panel retries at most once every RETRY_COOLDOWN; in
      // between, the viewer sees the error with its explicit "Tekrar dene" (which always retries).
      // Without this, sweeping the nav fires one request per hover while the endpoint is down.
      const retrying = failed && Date.now() - this.#errorAt > RETRY_COOLDOWN;
      panel = this.#megaPendingPanel({ reset: retrying });
      if (retrying) {
        this.#panelsState = 'idle';
        this.#loadPanels({ force: true });
      } else if (failed) {
        this.#renderPanelsError();
      } else {
        this.#loadPanels();
      }
      if (!panel) return;
    }

    if (!panel.contains(doc.activeElement)) this.#rescueFocus(handle);
    this.#hideAllPanels();
    panel.hidden = false;
    this.#openHandle = handle;
    this.#openedByHover = byHover ? Date.now() : 0;

    for (const item of this.#nav.querySelectorAll('.rod-hdr__nav-item')) {
      const opener = item.querySelector('[data-rod-mega-open]');
      const isOpen = !!opener && opener.getAttribute('data-rod-mega-open') === handle;
      item.classList.toggle('is-open', isOpen);
      if (opener && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', String(isOpen));
    }

    if (this.#overlay) {
      this.#overlay.hidden = false;
      requestAnimationFrame(() => this.#overlay.classList.add('is-visible'));
    }

    Rod.overlay.open('mega', {
      element: panel,
      close: () => this.#closeMega({ fromRegistry: true }),
    });

    this.#updateHeaderBottom();

    if (focusRail) {
      const first = panel.querySelector('.rod-mega__rail-link');
      if (first) first.focus();
    }
  }

  #hideAllPanels() {
    if (!this.#panelHost) return;
    for (const panel of this.#panelHost.children) panel.hidden = true;
  }

  #closeMega({ fromRegistry = false, restoreFocus = true } = {}) {
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#hoverTimer);
    if (!this.#openHandle) {
      if (!fromRegistry && Rod.overlay.isOpen('mega')) Rod.overlay.close('mega', { silent: true });
      return;
    }
    const closing = this.#openHandle;
    this.#openHandle = null;
    this.#openedByHover = 0;
    this.#pendingMega = null;
    // Focus never falls to <body> when the panel that holds it disappears (review fix round 2).
    if (restoreFocus) this.#rescueFocus(closing);
    this.#hideAllPanels();
    if (this.#nav) {
      for (const item of this.#nav.querySelectorAll('.rod-hdr__nav-item')) {
        item.classList.remove('is-open');
        const opener = item.querySelector('[data-rod-mega-open]');
        if (opener && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'false');
      }
    }
    if (this.#overlay) {
      this.#overlay.classList.remove('is-visible');
      this.#overlay.hidden = true;
    }
    if (!fromRegistry && Rod.overlay.isOpen('mega')) Rod.overlay.close('mega', { silent: true });
  }

  /** Behaviour of the lazily inserted mega panels (rail hover/focus, keyboard, close on tab-out). */
  #bindPanels() {
    if (!this.#panelHost) return;
    for (const panel of this.#panelHost.querySelectorAll('.rod-mega[data-rod-mega]')) {
      if (panel.dataset.rodBound === 'true') continue;
      panel.dataset.rodBound = 'true';

      // The hover bridge (pointerenter/pointerleave) lives on the panel HOST, see #setupNav.

      for (const rail of panel.querySelectorAll('.rod-mega__rail-link')) {
        const target = rail.getAttribute('data-rod-mega-target');
        if (!target) continue;
        rail.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch' || !HOVER.matches) return;
          window.clearTimeout(this.#railTimer);
          this.#railTimer = window.setTimeout(() => this.#activateView(panel, target), RAIL_DELAY);
        });
        rail.addEventListener('pointerleave', () => window.clearTimeout(this.#railTimer));
        rail.addEventListener('focus', () => this.#activateView(panel, target));
        /* A tap on an <a> focuses it BEFORE the click fires, and the focus handler above has then
           already set `is-active` — so reading the class in the click handler made the first tap
           navigate and an L4 list was unreachable on touch (SPEC 5.2.3). The pre-interaction state
           is therefore recorded on pointerdown. */
        rail.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'touch') return;
          rail.dataset.rodWasActive = rail.classList.contains('is-active') ? 'true' : 'false';
        });
        rail.addEventListener('click', (event) => {
          if (rail.tagName === 'BUTTON') {
            event.preventDefault();
            this.#activateView(panel, target);
            return;
          }
          const wasActive = rail.dataset.rodWasActive === 'true';
          delete rail.dataset.rodWasActive;
          if (this.#touch && !wasActive) {
            event.preventDefault();
            this.#activateView(panel, target);
          }
        });
      }

      panel.addEventListener('keydown', (event) => this.#panelKeydown(panel, event));

      panel.addEventListener('focusout', (event) => {
        // A focus rescue while panels are being switched/closed is not the viewer tabbing away.
        if (this.#switching) return;
        const next = event.relatedTarget;
        if (!next) return;
        if (panel.contains(next)) return;
        if (this.#nav && this.#nav.contains(next)) {
          const item = next.closest('.rod-hdr__nav-item');
          const opener = item ? item.querySelector('[data-rod-mega-open]') : null;
          if (opener && opener.getAttribute('data-rod-mega-open') === this.#openHandle) return;
        }
        this.#closeMega();
      });
    }
  }

  #activateView(panel, targetId) {
    const view = panel.querySelector(`#${CSS.escape(targetId)}`);
    if (!view) return;
    // Views share one grid cell (stable panel height); only visibility changes.
    for (const other of panel.querySelectorAll('[data-rod-mega-view]')) {
      other.classList.toggle('is-active', other === view);
    }
    for (const rail of panel.querySelectorAll('.rod-mega__rail-link')) {
      rail.classList.toggle('is-active', rail.getAttribute('data-rod-mega-target') === targetId);
    }
  }

  /** The tabbable elements of a panel in DOM order: rail, the ACTIVE view only (the others are
      `visibility: hidden`, so they are not tabbable), then the promos when rendered. */
  #panelFocusables(panel) {
    const nodes = [];
    const rail = panel.querySelector('[data-rod-mega-rail]');
    if (rail) nodes.push(...rail.querySelectorAll(FOCUSABLE));
    const view = panel.querySelector('[data-rod-mega-view].is-active');
    if (view) nodes.push(...view.querySelectorAll(FOCUSABLE));
    const promos = panel.querySelector('.rod-mega__promos:not([hidden])');
    if (promos) nodes.push(...promos.querySelectorAll(FOCUSABLE));
    return nodes;
  }

  /** First focusable of the document that follows the category nav (used when the open panel
      belongs to the LAST nav item, so Tab must leave the header entirely). */
  #focusAfterNav() {
    if (!this.#nav) return;
    for (const el of doc.querySelectorAll(FOCUSABLE)) {
      if (this.#nav.contains(el)) continue;
      if (!(this.#nav.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING)) continue;
      if (!el.getClientRects().length) continue;
      el.focus();
      return;
    }
  }

  #panelKeydown(panel, event) {
    // Tab out of the panel (SPEC 5.2.3): `.rod-hdr__panels` sits after the whole nav <ul>, so the
    // native order would skip every remaining L2 item. Forward Tab from the last panel element
    // closes the panel and continues on the NEXT nav item; Shift+Tab from the first returns to the
    // toggle that opened it.
    if (event.key === 'Tab' && this.#openHandle) {
      const focusables = this.#panelFocusables(panel);
      const index = focusables.indexOf(doc.activeElement);
      const atEnd = !event.shiftKey && index > -1 && index === focusables.length - 1;
      const atStart = event.shiftKey && index === 0;
      if (atEnd || atStart) {
        event.preventDefault();
        const opener = this.#openerFor(this.#openHandle);
        const item = opener ? opener.closest('.rod-hdr__nav-item') : null;
        this.#closeMega({ restoreFocus: false });
        if (atStart) {
          if (opener) opener.focus();
          return;
        }
        let next = item ? item.nextElementSibling : null;
        while (next && !next.querySelector(FOCUSABLE)) next = next.nextElementSibling;
        const target = next ? next.querySelector(FOCUSABLE) : null;
        if (target) target.focus();
        else this.#focusAfterNav();
        return;
      }
    }

    const rails = [...panel.querySelectorAll('.rod-mega__rail-link')];
    const inRail = rails.includes(doc.activeElement);
    const index = rails.indexOf(doc.activeElement);

    if (inRail && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      const target = rails[(next + rails.length) % rails.length];
      if (target) target.focus();
      return;
    }
    if (inRail && event.key === 'ArrowRight') {
      const view = panel.querySelector('[data-rod-mega-view].is-active');
      const first = view ? view.querySelector(FOCUSABLE) : null;
      if (first) {
        event.preventDefault();
        first.focus();
      }
      return;
    }
    if (!inRail && event.key === 'ArrowLeft') {
      const active = panel.querySelector('.rod-mega__rail-link.is-active') || rails[0];
      if (active) {
        event.preventDefault();
        active.focus();
      }
    }
  }

  #checkCompact() {
    if (!this.#nav || !this.#navList) return;
    if (!DESKTOP.matches) {
      this.#nav.classList.remove('is-compact');
      return;
    }
    this.#nav.classList.remove('is-compact');
    if (this.#navWraps()) this.#nav.classList.add('is-compact');
  }

  /* The nav list is `flex-wrap: wrap` on purpose (`overflow-x` would clip the mega panel), so an
     over-wide nav WRAPS instead of scrolling and `scrollWidth > clientWidth` is never true — the
     compact state never engaged and at 990–1099 the header showed a third band with a single
     orphan L2 on it (T-HDR-09, SPEC 15.3 "exactly two rows at every breakpoint"). Wrapping is
     detected from the item rows instead: more than one distinct top means a second line. */
  #navWraps() {
    const items = [];
    for (const li of this.#navList.children) {
      const rect = li.getBoundingClientRect();
      if (rect.height > 0) items.push(rect.top);
    }
    if (items.length < 2) return false;
    return items.some((top) => Math.abs(top - items[0]) > 1);
  }

  /* ============================================================ menu drawer (5.3) */
  #setupMenu() {
    const menu = this.#menu;
    if (!menu || !this.#menuBtn) return;

    this.#on(this.#menuBtn, 'click', () => this.#openMenu());
    if (this.#menuClose) this.#on(this.#menuClose, 'click', () => this.#requestMenuClose());
    if (this.#menuBack) this.#on(this.#menuBack, 'click', () => this.#popLevel());

    this.#on(menu, 'click', (event) => {
      if (event.target === menu) this.#requestMenuClose();
    });

    this.#on(menu, 'close', () => {
      menu.classList.remove('is-open');
      unlockScroll(menu);
      this.#menuBtn.setAttribute('aria-expanded', 'false');
      this.#resetLevels();
      if (Rod.overlay.isOpen('menu')) Rod.overlay.close('menu', { silent: true });
      // The trigger is `display: none` from 990 up, so after an auto-close on a resize past 990 a
      // focus() on it is a no-op and focus falls to <body> (review fix round 2). Focus the first
      // rendered header control instead.
      this.#restoreMenuFocus();
    });

    this.#on(this.#menuBody, 'click', (event) => {
      const row = event.target instanceof Element ? event.target.closest('[data-rod-menu-level]') : null;
      if (!row || !this.#menuBody.contains(row)) return;
      event.preventDefault();
      this.#enterLevel(row.getAttribute('data-rod-menu-level'), row.getAttribute('data-rod-menu-level-title') || '');
    });

    const onMedia = () => {
      if (DESKTOP.matches && menu.open) this.#requestMenuClose();
    };
    if (typeof DESKTOP.addEventListener === 'function') {
      DESKTOP.addEventListener('change', onMedia);
      this.#cleanup.push(() => DESKTOP.removeEventListener('change', onMedia));
    }
  }

  #openMenu() {
    const menu = this.#menu;
    if (!menu || menu.open) return;
    this.#closeMega();
    Rod.overlay.open('menu', {
      element: menu,
      history: true,
      close: () => {
        if (menu.open) menu.close();
      },
      onLevelBack: () => this.#popLevel({ fromHistory: true }),
    });
    lockScroll(menu);
    menu.showModal();
    requestAnimationFrame(() => menu.classList.add('is-open'));
    this.#menuBtn.setAttribute('aria-expanded', 'true');
    this.#resetLevels();
    if (this.#menuClose) this.#menuClose.focus({ preventScroll: true });
    this.#loadPanels();
  }

  /** Focus after the drawer closed: its trigger when it is rendered, otherwise the first visible
      header control (logo, then the desktop nav), so Tab never restarts at the top of the page. */
  #restoreMenuFocus() {
    const rendered = (el) => !!el && el.isConnected && el.getClientRects().length > 0;
    if (rendered(this.#menuBtn)) {
      this.#menuBtn.focus({ preventScroll: true });
      return;
    }
    const logo = this.querySelector('.rod-hdr__logo');
    if (rendered(logo)) {
      logo.focus({ preventScroll: true });
      return;
    }
    for (const el of this.querySelectorAll(FOCUSABLE)) {
      if (this.#menu && this.#menu.contains(el)) continue;
      if (!rendered(el)) continue;
      el.focus({ preventScroll: true });
      return;
    }
  }

  #requestMenuClose() {
    if (!this.#menu || !this.#menu.open) return;
    if (Rod.overlay.isOpen('menu')) Rod.overlay.close('menu');
    else this.#menu.close();
  }

  #closeMenu() {
    this.#requestMenuClose();
  }

  #resetLevels() {
    window.clearTimeout(this.#enterTimer);
    for (const level of this.#menuStack) {
      level.panel.classList.remove('is-active', 'is-entering');
      level.panel.hidden = true;
    }
    this.#menuStack = [];
    this.#clearMenuPending();
    this.#syncMenuBar();
    this.#syncPanelStates();
  }

  /** Only the topmost panel is interactive; the parents stay visible behind it but inert (a11y). */
  #syncPanelStates() {
    if (!this.#menuBody) return;
    const top = this.#menuStack.length ? this.#menuStack[this.#menuStack.length - 1].panel : null;
    const supportsInert = 'inert' in HTMLElement.prototype;
    for (const panel of this.#menuBody.querySelectorAll('.rod-menu__panel')) {
      const isTop = top ? panel === top : panel.dataset.rodMenuPanel === 'root';
      // `inert` already removes the subtree from the a11y tree; aria-hidden is only the fallback.
      if (supportsInert) panel.inert = !isTop;
      else if (isTop) panel.removeAttribute('aria-hidden');
      else panel.setAttribute('aria-hidden', 'true');
    }
  }

  #clearMenuPending() {
    if (this.#menuPending) {
      this.#menuPending.remove();
      this.#menuPending = null;
    }
  }

  #menuPendingPanel() {
    this.#clearMenuPending();
    const panel = doc.createElement('div');
    panel.className = 'rod-menu__panel';
    panel.setAttribute('data-rod-menu-pending', '');
    panel.appendChild(this.#skeletonBox('rod-menu__pending'));
    this.#menuBody.appendChild(panel);
    this.#menuPending = panel;
    return panel;
  }

  #enterLevel(handle, title, { replacePending = false } = {}) {
    if (!handle || !this.#menuBody) return;
    let panel = this.#menuBody.querySelector(`[data-rod-menu-panel="${CSS.escape(handle)}"]`);
    let pending = false;
    if (!panel) {
      if (this.#panelsState === 'ready') return;
      panel = this.#menuPendingPanel();
      pending = true;
      this.#pendingLevel = { handle, title };
      const failed = this.#panelsState === 'error';
      if (failed && Date.now() - this.#errorAt > RETRY_COOLDOWN) {
        this.#panelsState = 'idle';
        this.#loadPanels({ force: true });
      } else if (failed) {
        this.#renderPanelsError();
      } else {
        this.#loadPanels();
      }
    }

    if (replacePending) this.#menuStack.pop();

    panel.hidden = false;
    panel.style.zIndex = String(this.#menuStack.length + 1);
    // A second tap during the 250ms slide-in must not land on whatever row ends up under the finger
    // (the parent panel is already inert, so the tap is simply swallowed).
    panel.classList.add('is-entering');
    window.clearTimeout(this.#enterTimer);
    this.#enterTimer = window.setTimeout(
      () => panel.classList.remove('is-entering'),
      REDUCED.matches ? 0 : PANEL_MS
    );
    requestAnimationFrame(() => panel.classList.add('is-active'));
    this.#menuStack.push({ handle, title, panel, pending });
    this.#syncMenuBar();
    this.#syncPanelStates();
    if (!replacePending) Rod.overlay.pushLevel('menu');
    if (this.#menuBack) this.#menuBack.focus({ preventScroll: true });
    if (title) Rod.announce(`${title} menüsü`);
  }

  #popLevel({ fromHistory = false } = {}) {
    const top = this.#menuStack.pop();
    if (!top) return;
    window.clearTimeout(this.#enterTimer);
    top.panel.classList.remove('is-active', 'is-entering');
    const hide = () => {
      if (!top.panel.classList.contains('is-active')) {
        if (top.pending) {
          top.panel.remove();
          if (this.#menuPending === top.panel) this.#menuPending = null;
        } else {
          top.panel.hidden = true;
        }
      }
    };
    window.setTimeout(hide, REDUCED.matches ? 0 : PANEL_MS);
    this.#pendingLevel = null;
    this.#syncMenuBar();
    this.#syncPanelStates();
    if (!fromHistory) Rod.overlay.popLevel('menu');
    const focusTarget =
      this.#menuStack.length && this.#menuBack ? this.#menuBack : this.#menuClose;
    if (focusTarget) focusTarget.focus({ preventScroll: true });
  }

  #syncMenuBar() {
    const top = this.#menuStack[this.#menuStack.length - 1];
    if (this.#menuTitle) this.#menuTitle.textContent = top ? top.title : 'Menü';
    if (!this.#menuBack) return;
    if (!top) {
      this.#menuBack.hidden = true;
      return;
    }
    const parent = this.#menuStack[this.#menuStack.length - 2];
    const parentTitle = parent ? parent.title : 'Menü';
    this.#menuBack.hidden = false;
    this.#menuBack.setAttribute('aria-label', `Geri: ${parentTitle}`);
  }

  /* ============================================================ account dropdown (5.4) */
  #setupAccount() {
    const details = this.#account;
    if (!details) return;
    const summary = details.querySelector('summary');

    this.#on(details, 'toggle', () => {
      if (details.open) {
        Rod.overlay.open('account', {
          element: details,
          close: () => {
            details.open = false;
          },
        });
      } else if (Rod.overlay.isOpen('account')) {
        Rod.overlay.close('account', { silent: true });
      }
    });

    let openTimer = 0;
    let closeTimer = 0;
    this.#on(details, 'pointerenter', (event) => {
      if (event.pointerType === 'touch' || !HOVER.matches) return;
      window.clearTimeout(closeTimer);
      openTimer = window.setTimeout(() => {
        details.open = true;
      }, hoverIntentMs());
    });
    this.#on(details, 'pointerleave', (event) => {
      if (event.pointerType === 'touch' || !HOVER.matches) return;
      window.clearTimeout(openTimer);
      closeTimer = window.setTimeout(() => {
        if (!details.contains(doc.activeElement)) details.open = false;
      }, CLOSE_DELAY);
    });

    this.#on(doc, 'click', (event) => {
      if (!details.open) return;
      if (event.target instanceof Node && details.contains(event.target)) return;
      details.open = false;
    });

    this.#on(details, 'focusout', (event) => {
      const next = event.relatedTarget;
      if (!details.open || !next) return;
      if (!details.contains(next)) details.open = false;
    });

    this.#cleanup.push(
      Rod.onEsc(() => {
        if (!details.open) return false;
        details.open = false;
        if (summary) summary.focus();
        return true;
      })
    );
  }

  /* ============================================================ cart entry (5.1.6) */
  #setupCart() {
    // Seed from the server-rendered count so the FIRST cart update after a page load still bumps.
    const countEl = this.querySelector('[data-rod-cart-count]');
    const initial = countEl ? parseInt(String(countEl.textContent).replace(/\D+/g, ''), 10) : NaN;
    this.#lastCount = Number.isFinite(initial) ? initial : null;

    const btn = this.#cartBtn;
    if (btn && btn.hasAttribute('aria-expanded')) {
      this.#on(doc, 'theme-drawer:open', (event) => {
        if (event.target instanceof Element && event.target.id === 'cart-drawer') {
          btn.setAttribute('aria-expanded', 'true');
        }
      });
      this.#on(doc, 'theme-drawer:close', (event) => {
        if (event.target instanceof Element && event.target.id === 'cart-drawer') {
          btn.setAttribute('aria-expanded', 'false');
        }
      });
    }

    this.#on(doc, 'rod:cart:updated', (event) => {
      const count = Number(event.detail && event.detail.itemCount) || 0;
      const sr = this.querySelector('[data-rod-cart-sr]');
      if (sr) sr.textContent = `Sepetim, ${count} ürün`;
      if (this.#lastCount === null) {
        this.#lastCount = count;
        return;
      }
      if (count === this.#lastCount) return;
      this.#lastCount = count;
      if (!this.#cartIcon || REDUCED.matches) return;
      window.clearTimeout(this.#bumpTimer);
      this.#cartIcon.classList.remove('is-bumped');
      void this.#cartIcon.offsetWidth;
      this.#cartIcon.classList.add('is-bumped');
      this.#bumpTimer = window.setTimeout(() => this.#cartIcon.classList.remove('is-bumped'), 450);
    });
  }
}

if (!customElements.get('rod-header')) customElements.define('rod-header', RodHeader);
